import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import type { OrdreStatus } from "types/ordre";

const STATUS_OPTIONS: { value: OrdreStatus; label: string }[] = [
  { value: "a_preparer", label: "À préparer" },
  { value: "devis_initial", label: "Devis initial" },
  { value: "commande", label: "Commandé" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réceptionné" },
  { value: "en_cours", label: "En cours" },
  { value: "devis_supp", label: "Devis supp." },
  { value: "accord", label: "Accord" },
  { value: "acompte", label: "Acompte" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "solde", label: "Solde" },
  { value: "solde_paye", label: "Solde payé" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
];

/**
 * Status colors aligned with ServiceBox's palette — one distinct hue per status.
 * Statuses that have no ServiceBox equivalent use a complementary, well-separated
 * color. Used for the thick left border of planning events and for the status
 * chips (text/icon in this color, background a much lighter tint of it).
 */
const STATUS_COLORS: Record<string, string> = {
  a_preparer: "#6B7BE8",      // À réceptionner — bleu pervenche
  devis_initial: "#6B7280",   // devis en cours — gris
  commande: "#0EA5C4",        // (sans équivalent SB) — cyan
  pret: "#D6A310",            // dossier prêt — jaune/or
  reception: "#2563EB",       // réceptionné — bleu
  en_cours: "#DB2777",        // début travaux — magenta
  devis_supp: "#EA7317",      // (sans équivalent SB) — orange
  accord: "#8B9A2E",          // attente accord — olive
  acompte: "#E0609B",         // (sans équivalent SB) — rose
  fin_de_travaux: "#9333EA",  // fin travaux — violet
  facturation: "#F2613F",     // (sans équivalent SB) — corail
  solde: "#A87C4F",           // (sans équivalent SB) — brun
  solde_paye: "#16A34A",      // facturé — vert
  disponible: "#5DBB3A",      // disponible pour restitution — vert clair
  restitue: "#4E8C7A",        // restitué — vert sauge
};

const FALLBACK_STATUS_COLOR = "#6B7280";

export function getStatusColor(status: OrdreStatus): string {
  return STATUS_COLORS[status] || FALLBACK_STATUS_COLOR;
}

/** Mixes a hex color toward white. keep = share of the original color (0 → white, 1 → color). */
function mixWhite(hex: string, keep: number): string {
  const m = hex.replace(/^#/, "");
  const mix = (c: number) => Math.round(255 - (255 - c) * keep);
  const to = (n: number) => n.toString(16).padStart(2, "0");
  return `#${to(mix(parseInt(m.slice(0, 2), 16)))}${to(mix(parseInt(m.slice(2, 4), 16)))}${to(mix(parseInt(m.slice(4, 6), 16)))}`;
}

/** Short labels shown inside the status chips (more compact than the dropdown labels). */
const STATUS_CHIP_LABELS: Record<string, string> = {
  a_preparer: "en prépa",
  devis_initial: "devis initial",
  commande: "commande",
  pret: "prêt",
  reception: "réceptionné",
  en_cours: "en cours",
  devis_supp: "devis supp.",
  accord: "accord",
  acompte: "acompte",
  fin_de_travaux: "fin de travaux",
  facturation: "facturation",
  solde: "solde",
  solde_paye: "solde payé",
  disponible: "disponible",
  restitue: "restitué",
};

/** Chip style for a status: text/icon in the status color, background a light tint of it. */
export function getStatusChipStyle(status: string): { label: string; bg: string; color: string } {
  const color = getStatusColor(status as OrdreStatus);
  return {
    label: STATUS_CHIP_LABELS[status] || status,
    color,
    bg: mixWhite(color, 0.16),
  };
}

export const STATUS_CHIP_STYLES: Record<string, { label: string; bg: string; color: string }> =
  Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, getStatusChipStyle(s.value)]));

const STATUS_GROUPS = [
  { label: "Préparation", statuses: ["a_preparer", "devis_initial", "commande", "pret"] },
  { label: "Travaux", statuses: ["reception", "en_cours", "devis_supp", "accord", "acompte"] },
  { label: "Clôture", statuses: ["fin_de_travaux", "facturation", "solde", "solde_paye", "disponible", "restitue"] },
];

export default function OrdreStatusDropdown({
  currentStatus,
  onStatusChange,
  label,
}: {
  currentStatus: OrdreStatus;
  onStatusChange: (status: OrdreStatus) => void;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const DROPDOWN_HEIGHT = 320;

  const updatePos = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < DROPDOWN_HEIGHT && rect.top > DROPDOWN_HEIGHT) {
        setPos({ top: rect.top - DROPDOWN_HEIGHT - 4, left: rect.left });
      } else {
        setPos({ top: rect.bottom + 4, left: rect.left });
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePos();
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, updatePos]);

  const currentIdx = STATUS_OPTIONS.findIndex((s) => s.value === currentStatus);
  const chip = STATUS_CHIP_STYLES[currentStatus] || { label: currentStatus, bg: "#F4F4F4", color: "#A1A1A1" };

  return (
    <div ref={ref} className="relative flex flex-col gap-1">
      {label && (
        <span className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>
          {label}
        </span>
      )}
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        className="flex flex-row items-center gap-1.5 text-h3 font-medium cursor-pointer transition-opacity hover:opacity-80 w-fit"
        style={{ background: chip.bg, color: chip.color, borderRadius: 5, padding: "4px 14px", lineHeight: "18px" }}
      >
        {chip.label}
        <svg width={8} height={5} viewBox="0 0 8 5" fill="none">
          <path d="M1 1l3 3 3-3" stroke={chip.color} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && createPortal(
        <div
          ref={dropdownRef}
          className="bg-white rounded-xl shadow-lg border border-neutral-200 overflow-y-auto"
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 10001, minWidth: 190, maxHeight: DROPDOWN_HEIGHT, paddingTop: 4, paddingBottom: 4 }}
        >
          {STATUS_GROUPS.map((group, gi) => (
            <div key={group.label}>
              {gi > 0 && <div style={{ height: 1, background: "#F0F0F0", margin: "4px 12px" }} />}
              <div style={{ padding: "3px 12px 2px", fontFamily: "Poppins", fontSize: 10, fontWeight: 500, color: "#C0C0C0", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {group.label}
              </div>
              {group.statuses.map((sv) => {
                const idx = STATUS_OPTIONS.findIndex((s) => s.value === sv);
                const isCurrent = sv === currentStatus;
                const isPast = idx < currentIdx;
                const optChip = STATUS_CHIP_STYLES[sv] || { label: sv, bg: "#F4F4F4", color: "#A1A1A1" };
                return (
                  <button
                    key={sv}
                    className={`flex flex-row items-center w-full pr-3 text-left${isCurrent ? " cursor-default" : " hover:bg-neutral-50 transition-colors cursor-pointer"}`}
                    style={{ paddingLeft: 20, paddingTop: 2, paddingBottom: 2 }}
                    onClick={isCurrent ? undefined : () => { onStatusChange(sv as OrdreStatus); setOpen(false); }}
                    disabled={isCurrent}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: 9999,
                        background: getStatusColor(sv as OrdreStatus),
                        flexShrink: 0,
                        marginRight: 8,
                        opacity: isPast ? 0.45 : 1,
                      }}
                    />
                    <span
                      className={`whitespace-nowrap ${isCurrent ? "font-bold" : "font-medium"}`}
                      style={{
                        fontSize: 12,
                        color: isCurrent ? "#1A1A1A" : isPast ? "#C0C0C0" : "#666666",
                        fontFamily: "Poppins",
                      }}
                    >
                      {optChip.label}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

export { STATUS_OPTIONS };
